import React from 'react';
import { Avatar, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import './zo-avatar.scss';

export enum AvatarSizeVariants {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  EXTRALARGE = 'extraLarge',
}

interface ZoAvatarProps {
  title: string;
  variant?: AvatarSizeVariants;
  source?: string;
  shape?: 'round' | 'square';
}

const StyledAvatar = styled(Avatar)(
  ({ shape }: { shape: 'round' | 'square' }) => ({
    borderRadius: shape === 'round' ? '50%' : '8px',
  })
);

const AVATAR_CONFIG = {
  [AvatarSizeVariants.SMALL]: { size: 20, font: 8 },
  [AvatarSizeVariants.MEDIUM]: { size: 28, font: 10 },
  [AvatarSizeVariants.LARGE]: { size: 32, font: 12 },
  [AvatarSizeVariants.EXTRALARGE]: { size: 38, font: 14 },
};

export const ZoAvatar: React.FC<ZoAvatarProps> = ({
  title = 'Unknown',
  variant = AvatarSizeVariants.LARGE,
  source,
  shape = 'round',
}) => {
  const backgroundColors = [
    '#DBDBFA',
    '#E2F1F8',
    '#DCEADF',
    '#DCE1EA',
    '#FFD1DC',
    '#CADEE8',
    '#E2F8F5',
    '#EADCDC',
    '#E2E2F8',
    '#FADBDB',
    '#EADCDF',
    '#EBEBFA',
    '#D1F0FF',
    '#E9F1EB',
    '#E2EBF8',
    '#EEDED8',
    '#DCE5EA',
    '#EBFAF6',
    '#F8E2E2',
    '#EBF1FA',
    '#CAE8CF',
    '#EFE9E6',
    '#DBE7FA',
    '#F8E9E2',
    '#DCDCEA',
    '#FAEBEB',
    '#DCEAE8',
    '#FDEAE3',
    '#F8E2E7',
    '#DCEAE8',
    '#F8E7EB',
    '#E2F8E7',
  ];

  // Generate a background color based on the title
  // const getBackgroundColor = (str: string): string => {
  //   const hash = Array.from(str).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  //   return backgroundColors[hash % backgroundColors.length];
  // };
  const getBackgroundColor = (str?: string): string => {
    if (!str) return backgroundColors[0]; // Default color if `str` is empty
    const hash = Array.from(str).reduce(
      (acc, char) => acc + char.charCodeAt(0),
      0
    );
    return backgroundColors[hash % backgroundColors.length];
  };

  const bgColor = getBackgroundColor(title);

  // ✅ RESPONSIVE HANDLING (only for EXTRALARGE)
  const isSmallScreen = useMediaQuery('(max-width:1440px)');

  const finalVariant =
    variant === AvatarSizeVariants.EXTRALARGE && isSmallScreen
      ? AvatarSizeVariants.LARGE
      : variant;

  // ✅ CLEAN SIZE ACCESS
  const { size: avatarSize, font: fontSize } = AVATAR_CONFIG[finalVariant];

  // const avatarSize =
  //   variant === AvatarSizeVariants.SMALL
  //     ? 20
  //     : variant === AvatarSizeVariants.MEDIUM
  //       ? 28
  //       : variant === AvatarSizeVariants.LARGE
  //         ? 32
  //         : variant === AvatarSizeVariants.EXTRALARGE
  //           ? 38
  //           : 32;
  // // const fontSize = variant === AvatarSizeVariants.SMALL ? 8 : 12;
  // const fontSize =
  //   variant === AvatarSizeVariants.SMALL
  //     ? 8
  //     : variant === AvatarSizeVariants.MEDIUM
  //       ? 10
  //       : variant === AvatarSizeVariants.LARGE
  //         ? 12
  //         : variant === AvatarSizeVariants.EXTRALARGE
  //           ? 14
  //           : 12;

  // Logic to determine the initials
  const getInitials = (name: string): string => {
    const words = name.trim().split(/\s+/); // Split by spaces
    return words.length > 1
      ? `${words[0][0]}${words[1][0]}`.toUpperCase() // First letter of the first and second word
      : name.slice(0, 2).toUpperCase(); // First two letters of the single word
  };

  const initials = getInitials(title);

  return source ? (
    <StyledAvatar
      src={source}
      alt={title}
      shape={shape}
      sx={{
        width: avatarSize,
        height: avatarSize,
      }}
    />
  ) : (
    <StyledAvatar
      className="zoAvatar-container"
      shape={shape}
      sx={{
        width: avatarSize,
        height: avatarSize,
        backgroundColor: bgColor,
        fontSize,
        color: '#262626',
        textTransform: 'uppercase',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Segoe-UI-VF',
      }}
    >
      {initials}
    </StyledAvatar>
  );
};
